import { DB_NAME, HOST } from './config';
import {
  buildExport,
  forgetIdentity,
  setActive,
  upsertIdentity,
  validateExport,
} from './identityStore';
import {
  notifyIdentityStoreUpdate,
  setActiveTokenHash,
  setSessionState,
  useLocalIdentities,
  useNavigate,
} from './hooks';

const panel: React.CSSProperties = {
  background: '#0e1320',
  color: '#e7d9b6',
  padding: 24,
  borderRadius: 8,
  width: 480,
  maxWidth: '92vw',
  fontFamily: 'system-ui, sans-serif',
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderRadius: 4,
  background: '#161d2e',
  marginBottom: 8,
};

const buttonStyle: React.CSSProperties = {
  background: '#2a3445',
  color: '#e7d9b6',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
};

const primaryButton: React.CSSProperties = {
  ...buttonStyle,
  background: '#e09020',
  color: '#1a1208',
};

function formatLastUsed(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ms).toLocaleDateString();
}

export function IdentityPicker() {
  const store = useLocalIdentities();
  const navigate = useNavigate();

  const useIdentity = (hash: string) => {
    setActive(HOST, DB_NAME, hash);
    setActiveTokenHash(hash);
    setSessionState('CONNECTING');
    notifyIdentityStoreUpdate();
  };

  const createNew = () => {
    // Empty active hash → guard will mount provider with no token; STDB will
    // assign a fresh anon identity. The connect callback (in SessionGuard)
    // upserts it.
    setActive(HOST, DB_NAME, null);
    setActiveTokenHash('');
    setSessionState('CONNECTING');
    notifyIdentityStoreUpdate();
  };

  const forget = (hash: string, label: string) => {
    if (
      !window.confirm(
        `Forget "${label}"? This is irrecoverable unless you've exported it.`
      )
    ) {
      return;
    }
    forgetIdentity(HOST, DB_NAME, hash);
    notifyIdentityStoreUpdate();
  };

  const exportIdentity = async (hash: string) => {
    const id = store.identities.find(i => i.tokenHash === hash);
    if (!id) return;
    const payload = await buildExport(id);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    // Prefer the squirrel name (more meaningful than "Identity-1");
    // sanitize to keep the filename portable.
    const filenameName = (id.lastKnownUserName ?? id.localLabel).replace(
      /[^A-Za-z0-9_-]/g,
      '_'
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `heartwood-${filenameName}.json`;
    // Some browsers refuse to honor a programmatic click on a detached
    // anchor; appending it briefly to the document avoids that gotcha.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importIdentity = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const valid = await validateExport(parsed);
        await upsertIdentity(HOST, DB_NAME, valid.token, {
          localLabel: valid.localLabel,
          lastKnownUserName: valid.lastKnownUserName,
          lastKnownIsAdmin: valid.lastKnownIsAdmin,
        });
        notifyIdentityStoreUpdate();
      } catch (e) {
        window.alert(
          `Import failed: ${e instanceof Error ? e.message : String(e)}`
        );
      }
    };
    input.click();
  };

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0e18',
      }}
    >
      <div style={panel}>
        <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>Pick a squirrel</h2>
        <p style={{ opacity: 0.65, fontSize: 13, marginBottom: 16 }}>
          You have a few identities saved on this device. Pick one to play as,
          or start fresh.
        </p>
        {store.identities.length === 0 && (
          <p style={{ opacity: 0.6, fontStyle: 'italic' }}>No saved identities.</p>
        )}
        {store.identities.map(id => (
          <div key={id.tokenHash} style={row}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14 }}>
                {id.lastKnownUserName ?? id.localLabel}
                {id.lastKnownIsAdmin && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      background: '#e09020',
                      color: '#1a1208',
                      padding: '1px 6px',
                      borderRadius: 3,
                    }}
                  >
                    ADMIN
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>
                {formatLastUsed(id.lastUsedAt)}
              </div>
            </div>
            <button
              style={primaryButton}
              onClick={() => useIdentity(id.tokenHash)}
            >
              Use
            </button>
            <button style={buttonStyle} onClick={() => exportIdentity(id.tokenHash)}>
              Export
            </button>
            <button
              style={buttonStyle}
              onClick={() => forget(id.tokenHash, id.localLabel)}
            >
              Forget
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button style={primaryButton} onClick={createNew}>
            + Create new identity
          </button>
          <button style={buttonStyle} onClick={importIdentity}>
            Import from file…
          </button>
          <div style={{ flex: 1 }} />
          <button style={buttonStyle} onClick={() => navigate('/')}>
            Back to landing
          </button>
        </div>
      </div>
    </div>
  );
}
